import type { Bounds } from 'quill/core';
import Quill, { Delta } from 'quill/core';
import React, { useState } from 'react';
import DropdownMenu from '../../../components/DropdownMenu';
import { MenuItemRadio } from '../../../components/Menu';
import SVGIcon from '../../../components/SVGIcon';
import textIcon from '../../../icons/non-prefilled/16x16/text.svg';
import hintErrorIcon from '../../../icons/prefilled/16x16/hintError.svg';
import hintInfoIcon from '../../../icons/prefilled/16x16/hintInfo.svg';
import hintSuccessIcon from '../../../icons/prefilled/16x16/hintSuccess.svg';
import hintWarningIcon from '../../../icons/prefilled/16x16/hintWarning.svg';
import type { HoveredBlotListenerSetting } from '../../HoveredBlotListener';
import { useHoveredBlotListener } from '../../HoveredBlotListener';
import { useQuill, useQuillReadOnly } from '../../QuillContext';
import type { HintType } from '../../formats/Hint';
import Hint, { HINT_LABELS } from '../../formats/Hint';
import type { Blot } from '../../types';
import getItemAllLines from '../../utils/getItemAllLines';
import getReferenceNodeForBounds from '../../utils/getReferenceNodeForBounds';
import {
  HintDangerItem,
  HintInfoItem,
  HintSuccessItem,
  HintWarningItem,
} from '../getSlashCommandMenuSections';
import FloatingToolbar from './FloatingToolbar';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import getRelativeBoundingRect from './getRelativeBoundingRect';

const HINT_ICON: Record<HintType, string> = {
  info: hintInfoIcon,
  success: hintSuccessIcon,
  warning: hintWarningIcon,
  error: hintErrorIcon,
};

const items = [
  HintInfoItem,
  HintSuccessItem,
  HintWarningItem,
  HintDangerItem,
  {
    id: 'plain',
    data: {
      type: null,
      label: 'Normal text',
      icon: <SVGIcon>{textIcon}</SVGIcon>,
    },
  },
];

interface BaseHintToolbarProps {
  type: HintType;
  onTypeChange: (type: HintType | null) => void;
  setPersist: (persist: boolean) => void;
  initialDropdownOpen?: boolean;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseHintToolbar = ({
  type,
  onTypeChange,
  setPersist,
  initialDropdownOpen = false,
}: BaseHintToolbarProps) => {
  const [index, setIndex] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(initialDropdownOpen);

  return (
    <ToolbarButton
      icon={HINT_ICON[type]}
      rightAlignPopOver
      renderPopOver={(onClose) => (
        <DropdownMenu.Root<{
          icon: React.ReactNode;
          label: string;
          type: HintType | null;
        }>
          index={index}
          withPadding
          onIndexChange={setIndex}
          onClose={onClose}
          onSelect={(item) => {
            onTypeChange(item.data.type);
            setIsDropdownOpen(false);
            setPersist(false);
          }}
          inputContainer={null}
          sections={[{ items }]}
          renderItem={(data) => (
            <MenuItemRadio icon={data.icon} checked={data.type === type}>
              {data.label}
            </MenuItemRadio>
          )}
        />
      )}
      popOverOpen={isDropdownOpen}
      onPopOverOpenChange={(open) => {
        setIsDropdownOpen(open);
        setPersist(open);
        if (!open) setIndex(null);
      }}
      aria-label="Hint style"
    >
      {HINT_LABELS[type]}
    </ToolbarButton>
  );
};

type Context = {
  type: HintType;
  hintBlots: Hint[];
  bounds: Bounds | null;
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<Context> = {
  isHoveredBlot(blot: Blot) {
    return blot.statics.blotName === Hint.blotName;
  },
  getContext(blot: Hint, quill: Quill) {
    const hintBlots = getItemAllLines(blot);
    const bounds = hintBlots[0]
      ? getRelativeBoundingRect(
          getReferenceNodeForBounds(hintBlots[0]),
          quill.container,
        )
      : null;
    return {
      type: blot.formats()[Hint.blotName] as HintType,
      hintBlots,
      bounds,
    };
  },
};

const HintToolbar = () => {
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();

  const { context, setPersist } = useHoveredBlotListener(
    quill,
    hoveredBlotListenerSetting,
  );

  const { type, bounds = null, hintBlots = [] } = context ?? {};

  if (readOnly || !type) {
    return null;
  }

  const updateHintType = (newType: HintType | null) => {
    const first = hintBlots[0];
    if (!first || !quill) {
      return;
    }

    const delta = hintBlots.reduce(
      (acc, blot) =>
        acc.retain(blot.length() - 1).retain(1, { [Hint.blotName]: newType }),
      new Delta().retain(first.offset(first.scroll)),
    );

    const range = quill.getSelection();
    quill.updateContents(delta, Quill.sources.USER);
    if (range) quill.setSelection(range, Quill.sources.SILENT);
  };

  return (
    <FloatingToolbar.Toolbar bounds={bounds}>
      <ToolbarButtonGroup>
        <BaseHintToolbar
          type={type}
          onTypeChange={updateHintType}
          setPersist={setPersist}
        />
      </ToolbarButtonGroup>
    </FloatingToolbar.Toolbar>
  );
};

export default HintToolbar;
